import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Rocket, ArrowRight, ArrowLeft, Zap, Shield, Clock, Brain, BarChart3, Users, Flame, CheckCircle, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

const TESTS = [
  {
    id: 'pain',
    number: 1,
    title: 'The Pain Score',
    question: 'Are people losing money, time, or sleep because this problem exists right now?',
    insight: 'The best startups don\'t create demand. They find pain that already keeps someone up at night.',
    icon: Zap,
    color: 'from-red-500 to-orange-500',
    options: [
      { label: 'Mild inconvenience', desc: 'People notice it but work around it easily', points: 0 },
      { label: 'Recurring frustration', desc: 'People complain about it regularly and waste time on it', points: 10 },
      { label: 'Already paying to fix it', desc: 'People spend money on workarounds or inferior solutions', points: 20 },
    ],
  },
  {
    id: 'vitamin_painkiller',
    number: 2,
    title: 'Vitamin or Painkiller',
    question: 'Is your solution a nice-to-have or a must-have?',
    insight: 'Vitamins get tried once. Painkillers get subscribed to. Which one are you building?',
    icon: Shield,
    color: 'from-purple-500 to-pink-500',
    options: [
      { label: 'Nice to have (Vitamin)', desc: 'Users might try it but won\'t miss it if gone', points: 0 },
      { label: 'Strong preference', desc: 'Users would be annoyed if they lost access', points: 10 },
      { label: 'Cannot function without it (Painkiller)', desc: 'Users would scramble to find a replacement immediately', points: 20 },
    ],
  },
  {
    id: 'timing',
    number: 3,
    title: 'India Timing Score',
    question: 'Is India ready for this right now?',
    insight: 'Flipkart failed once. Then came back when India was ready. Timing isn\'t luck. It\'s research.',
    icon: Clock,
    color: 'from-amber-500 to-yellow-500',
    options: [
      { label: 'Too early', desc: 'Infrastructure doesn\'t exist yet, behaviour is unfamiliar', points: 0 },
      { label: 'Market is emerging', desc: '2\u20133 years away from mainstream adoption', points: 10 },
      { label: 'Behaviour already exists', desc: 'You\'re just formalising what people already do informally', points: 20 },
    ],
  },
  {
    id: 'unfair_advantage',
    number: 4,
    title: 'Unfair Advantage Score',
    question: 'Why are YOU the right person to build this?',
    insight: 'Anyone can have an idea. The question is: why can\'t someone with \u20B9100 crore just copy you in 90 days?',
    icon: Brain,
    color: 'from-cyan-500 to-blue-500',
    options: [
      { label: 'No specific advantage', desc: 'Anyone with resources could build this', points: 0 },
      { label: 'Domain knowledge or network', desc: 'You understand the space deeply or know the right people', points: 10 },
      { label: 'Unique access, IP, or lived experience', desc: 'Nobody else has what you bring to this problem', points: 20 },
    ],
  },
  {
    id: 'market_size',
    number: 5,
    title: 'Market Size Score',
    question: 'Is the market large enough to build a \u20B9100 crore business inside it?',
    insight: 'Small markets make great projects. Not great startups.',
    icon: BarChart3,
    color: 'from-green-500 to-emerald-500',
    options: [
      { label: 'Niche with no expansion path', desc: 'Limited addressable market, hard to scale', points: 0 },
      { label: '\u20B9500 crore TAM with adjacencies', desc: 'Room to grow into related segments', points: 10 },
      { label: '\u20B95,000 crore+ TAM with clear entry wedge', desc: 'Massive market with a specific way in', points: 20 },
    ],
  },
  {
    id: 'customer_conversations',
    number: 6,
    title: 'Customer Conversation Score',
    question: 'Have you talked to 10 real potential customers before building anything?',
    insight: 'Your friends will not tell you your idea is bad. Strangers will. Talk to strangers.',
    icon: Users,
    color: 'from-blue-500 to-indigo-500',
    options: [
      { label: 'Zero conversations', desc: 'Haven\'t spoken to anyone outside your circle', points: 0 },
      { label: 'Talked to friends and family', desc: 'Got feedback but from people who want to be nice', points: 5 },
      { label: '10+ conversations with strangers who have the problem', desc: 'Real validation from people with no reason to lie', points: 20 },
    ],
  },
  {
    id: 'obsession',
    number: 7,
    title: 'The Obsession Test',
    question: 'Will you still want to work on this after 3 years of no salary, 2 near-death moments, and 1 co-founder fight?',
    insight: 'Ideas don\'t build companies. Obsession does.',
    icon: Flame,
    color: 'from-orange-500 to-red-600',
    options: [
      { label: 'Probably not', desc: 'It\'s interesting but you\'d move on if it got hard', points: 0 },
      { label: 'Maybe', desc: 'You\'re committed but have backup plans', points: 10 },
      { label: 'Can\'t stop thinking about it', desc: 'Even when you try to take a break, it pulls you back', points: 20 },
    ],
  },
];

const getVerdict = (score) => {
  if (score >= 80) return { range: '80\u2013100', title: 'BUILD IT NOW', color: 'text-green-400', bg: 'from-green-500/20 to-emerald-500/20 border-green-500/40', desc: 'Quit the job. Talk to investors. Time is your only enemy.' };
  if (score >= 60) return { range: '60\u201379', title: 'BUILD WITH CAUTION', color: 'text-amber-400', bg: 'from-amber-500/20 to-yellow-500/20 border-amber-500/40', desc: 'Validate one more test. Your idea is good. Your timing may not be.' };
  if (score >= 40) return { range: '40\u201359', title: 'REFINE BEFORE YOU BUILD', color: 'text-orange-400', bg: 'from-orange-500/20 to-red-500/20 border-orange-500/40', desc: 'One or two tests are broken. Fix those first.' };
  return { range: 'Below 40', title: 'DON\'T BUILD YET', color: 'text-red-400', bg: 'from-red-500/20 to-rose-500/20 border-red-500/40', desc: 'This isn\'t your idea. Or this isn\'t your moment. Both are okay.' };
};

const IdeaValidation = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = intro, 1-7 = tests, 8 = results
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [ideaName, setIdeaName] = useState('');

  const totalScore = Object.values(answers).reduce((sum, a) => sum + a.points, 0);
  const maxScore = TESTS.length * 20;

  const handleSelect = (testId, option) => {
    setAnswers(prev => ({ ...prev, [testId]: option }));
  };

  const handleNext = () => {
    if (step === 0 && !ideaName.trim()) return;
    setStep(prev => prev + 1);
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/idea-validation`, {
        idea_name: ideaName,
        answers: Object.entries(answers).map(([testId, opt]) => ({
          test_id: testId,
          selected: opt.label,
          points: opt.points,
        })),
        total_score: totalScore,
        verdict: getVerdict(totalScore).title,
      }, { withCredentials: true });
    } catch {}
    setSaving(false);
    setStep(8);
  };

  const currentTest = step >= 1 && step <= 7 ? TESTS[step - 1] : null;
  const currentAnswer = currentTest ? answers[currentTest.id] : null;

  // Intro screen
  if (step === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-6" data-testid="idea-validation-intro">
        <div className="max-w-xl w-full text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Rocket className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-100 mb-3">Idea Validation</h1>
          <p className="text-slate-400 mb-8 text-lg">7 tests. 140 points. One honest answer: should you build this?</p>

          <div className="mb-8">
            <label className="text-sm text-slate-500 block mb-2 text-left">What's your startup idea? (one line)</label>
            <input
              type="text"
              value={ideaName}
              onChange={(e) => setIdeaName(e.target.value)}
              placeholder="e.g. AI-powered hiring for blue-collar workers"
              className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-4 py-3 text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50"
              data-testid="idea-name-input"
              onKeyDown={(e) => e.key === 'Enter' && ideaName.trim() && handleNext()}
            />
          </div>

          <Button
            onClick={handleNext}
            disabled={!ideaName.trim()}
            data-testid="start-validation-btn"
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-3 text-base rounded-full disabled:opacity-40"
          >
            Start Validation <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          <button onClick={() => navigate('/dashboard')} className="block mx-auto mt-6 text-sm text-slate-600 hover:text-slate-400 transition-colors">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Results screen
  if (step === 8) {
    const verdict = getVerdict(totalScore);
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6" data-testid="idea-validation-results">
        <div className="max-w-2xl mx-auto pt-12">
          <div className="text-center mb-10">
            <CheckCircle className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-100 mb-1">{ideaName}</h1>
            <p className="text-slate-500">Idea Validation Complete</p>
          </div>

          {/* Score */}
          <Card className={`bg-gradient-to-br ${verdict.bg} border p-8 text-center mb-8`}>
            <div className="text-6xl font-bold text-slate-100 mb-2">{totalScore}<span className="text-2xl text-slate-500">/{maxScore}</span></div>
            <div className={`text-2xl font-bold ${verdict.color} mb-3`}>{verdict.title}</div>
            <p className="text-slate-300 text-base max-w-md mx-auto">{verdict.desc}</p>
          </Card>

          {/* Breakdown */}
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">Score Breakdown</h3>
          <div className="space-y-3 mb-10">
            {TESTS.map((test) => {
              const ans = answers[test.id];
              const Icon = test.icon;
              return (
                <div key={test.id} className="flex items-center gap-3 bg-slate-800/40 border border-slate-700 rounded-lg px-4 py-3" data-testid={`result-${test.id}`}>
                  <Icon className="w-5 h-5 text-slate-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-slate-300">{test.title}</span>
                    <span className="text-xs text-slate-600 ml-2">{ans ? ans.label : 'Skipped'}</span>
                  </div>
                  <span className={`text-sm font-bold ${ans?.points === 20 ? 'text-green-400' : ans?.points >= 10 ? 'text-amber-400' : 'text-red-400'}`}>
                    {ans?.points ?? 0}/{20}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="flex gap-4 justify-center">
            <Button
              onClick={() => { setStep(0); setAnswers({}); setIdeaName(''); }}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="validate-another-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Validate Another Idea
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
              data-testid="back-to-dashboard-btn"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Test screens (1-7)
  const Icon = currentTest.icon;
  const progress = (step / TESTS.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6" data-testid={`idea-validation-step-${step}`}>
      <div className="max-w-2xl mx-auto pt-8">
        {/* Progress */}
        <div className="flex items-center gap-3 mb-8">
          <span className="text-sm text-slate-500 shrink-0">Test {step}/7</span>
          <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-sm text-slate-500 shrink-0">{totalScore} pts</span>
        </div>

        {/* Test Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-10 h-10 bg-gradient-to-br ${currentTest.color} rounded-lg flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-slate-100">{currentTest.title}</h2>
        </div>

        <p className="text-lg text-slate-300 mb-3">{currentTest.question}</p>
        <p className="text-sm text-slate-600 italic mb-8 border-l-2 border-slate-700 pl-3">{currentTest.insight}</p>

        {/* Options */}
        <div className="space-y-3 mb-10">
          {currentTest.options.map((opt, i) => {
            const isSelected = currentAnswer?.label === opt.label;
            return (
              <button
                key={i}
                onClick={() => handleSelect(currentTest.id, opt)}
                data-testid={`option-${currentTest.id}-${i}`}
                className={`w-full text-left p-4 rounded-lg border transition-all duration-200 ${
                  isSelected
                    ? 'bg-cyan-500/10 border-cyan-500/50 ring-1 ring-cyan-500/30'
                    : 'bg-slate-800/40 border-slate-700 hover:border-slate-600 hover:bg-slate-800/60'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-medium ${isSelected ? 'text-cyan-300' : 'text-slate-200'}`}>{opt.label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    opt.points === 20 ? 'bg-green-500/10 text-green-400' :
                    opt.points >= 10 ? 'bg-amber-500/10 text-amber-400' :
                    'bg-red-500/10 text-red-400'
                  }`}>{opt.points} pts</span>
                </div>
                <p className="text-sm text-slate-500">{opt.desc}</p>
              </button>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            onClick={() => setStep(prev => prev - 1)}
            variant="ghost"
            className="text-slate-400 hover:text-slate-200"
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          {step < 7 ? (
            <Button
              onClick={handleNext}
              disabled={!currentAnswer}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 disabled:opacity-40"
              data-testid="next-step-btn"
            >
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleFinish}
              disabled={!currentAnswer || saving}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:opacity-40"
              data-testid="finish-validation-btn"
            >
              {saving ? 'Saving...' : 'See My Score'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default IdeaValidation;
